import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { ColumnWidthOutlined } from '@ant-design/icons';
import { stopDefaultEvents } from '../../utils/DataAccessUtils';

export interface ResizableSplitPanelProps {
	leftPanel: React.ReactNode;
	rightPanel: React.ReactNode;
	defaultLeftPercent?: number;
	minLeftPercent?: number;
	maxLeftPercent?: number;
	height?: string | number;
	className?: string;
	leftPanelStyle?: React.CSSProperties;
	rightPanelStyle?: React.CSSProperties;
	rightPanelClassName?: string;
	leftPanelClassName?: string;
}

const DEFAULT_LEFT_PERCENT = 50;
const DEFAULT_MIN = 20;
const DEFAULT_MAX = 80;

export const ResizableSplitPanel: React.FC<ResizableSplitPanelProps> = ({
	leftPanel,
	rightPanel,
	defaultLeftPercent = DEFAULT_LEFT_PERCENT,
	minLeftPercent = DEFAULT_MIN,
	maxLeftPercent = DEFAULT_MAX,
	height = '100%',
	className = '',
	leftPanelStyle = {},
	rightPanelStyle = {},
	leftPanelClassName = '',
	rightPanelClassName = '',
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [leftPanelPercent, setLeftPanelPercent] = useState(defaultLeftPercent);
	const [isDragging, setIsDragging] = useState(false);

	const handleResizeStart = useCallback((e: React.MouseEvent) => {
		stopDefaultEvents(e);
		setIsDragging(true);
	}, []);

	useEffect(() => {
		if (!isDragging) return;
		const handleMouseMove = (e: MouseEvent) => {
			const container = containerRef.current;
			if (!container) return;
			const rect = container.getBoundingClientRect();
			const percent = ((e.clientX - rect.left) / rect.width) * 100;
			const clamped = Math.max(minLeftPercent, Math.min(maxLeftPercent, percent));
			setLeftPanelPercent(clamped);
		};
		const handleMouseUp = () => setIsDragging(false);
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [isDragging, minLeftPercent, maxLeftPercent]);

	const containerHeight = typeof height === 'number' ? `${height}px` : height;

	return (
		<div
			ref={containerRef}
			className={`flex ${className}`}
			style={{
				minHeight: containerHeight,
				height: containerHeight,
			}}
		>
			<div
				className={`flex min-w-0 flex-col ${leftPanelClassName}`}
				style={{
					flex: `0 0 ${leftPanelPercent}%`,
					...leftPanelStyle,
				}}
			>
				{leftPanel}
			</div>
			<div
				role="separator"
				aria-orientation="vertical"
				className="relative flex w-2 shrink-0 items-start justify-center bg-transparent"
			>
				<Button
					type={isDragging ? 'primary' : 'default'}
					shape="circle"
					icon={<ColumnWidthOutlined />}
					onMouseDown={handleResizeStart}
					aria-label="Resize panels"
					className="absolute left-1/2 top-6 z-10 -translate-x-1/2 cursor-col-resize"
				/>
			</div>
			<div
				className={`min-w-0 ${rightPanelClassName}`}
				style={{
					...rightPanelStyle,
				}}
			>
				{rightPanel}
			</div>
		</div>
	);
};

export default ResizableSplitPanel;
